import type { DropZoneProps as AriaDropZoneProps } from "react-aria-components";

import type { ReactNode } from "react";

import { useCallback, useRef, useState } from "react";

import clsx from "clsx";
import {
	Button as AriaButton,
	DropZone as AriaDropZone,
	FileTrigger,
	Label,
} from "react-aria-components";
import { useController, useFormContext } from "react-hook-form";

import { FileIcon } from "../../icons/file";
import { Spinner } from "../spinner";

import styles from "./drop-zone.module.css";

type DropEvent = Parameters<NonNullable<AriaDropZoneProps["onDrop"]>>[0];

type DropZoneProps = {
	/**
	 * MIME types to accept (e.g. ["image/png", "application/pdf"]).
	 */
	acceptedFileTypes?: string[];
	isDisabled?: boolean;
	isLoading?: boolean;
	label: string;
	name: string;
	onFile?: (file: File) => void;
};

function isFileTypeAllowed(file: File, acceptedFileTypes?: string[]): boolean {
	if (!acceptedFileTypes || acceptedFileTypes.length === 0) {
		return true;
	}
	return acceptedFileTypes.includes(file.type);
}

type DropZoneContentProps = {
	acceptedFileTypes?: string[];
	isDisabled: boolean;
	isLoading: boolean;
	onFile: (file: File) => void;
	onRemove: () => void;
	selectedFileName: null | string;
};

function DropZoneContent({
	acceptedFileTypes,
	isDisabled,
	isLoading,
	onFile,
	onRemove,
	selectedFileName,
}: DropZoneContentProps): ReactNode {
	if (isLoading) {
		return <Spinner className={styles.spinner} />;
	}

	if (selectedFileName) {
		return (
			<div className={styles.selectedState}>
				<FileIcon className={styles.fileIcon} />
				<span className={clsx("body-2", styles.fileName)}>{selectedFileName}</span>
				<AriaButton
					className={clsx("caption", styles.removeButton, isDisabled && styles.disabled)}
					isDisabled={isDisabled}
					onClick={onRemove}
					type="button"
				>
					Remove
				</AriaButton>
			</div>
		);
	}

	return (
		<div className={styles.emptyState}>
			<span className={clsx("body-2", styles.dragText)}>Drag &amp; Drop or</span>
			<FileTrigger
				acceptedFileTypes={acceptedFileTypes}
				onSelect={(fileList) => {
					const file = fileList?.item(0);
					if (file) {
						onFile(file);
					}
				}}
			>
				<AriaButton
					className={clsx("body-2", styles.browseButton, isDisabled && styles.disabled)}
					isDisabled={isDisabled}
					type="button"
				>
					choose a file
				</AriaButton>
			</FileTrigger>
			{acceptedFileTypes && acceptedFileTypes.length > 0 && (
				<span className={clsx("caption", styles.hint)}>
					{`Supported files: ${acceptedFileTypes
						.map((t) => {
							return t.split("/").pop()?.toUpperCase();
						})
						.join(", ")}`}
				</span>
			)}
		</div>
	);
}

export function DropZone({
	acceptedFileTypes,
	isDisabled = false,
	isLoading = false,
	label,
	name,
	onFile,
}: DropZoneProps): ReactNode {
	const { clearErrors, control, setError } = useFormContext();
	const { field, fieldState } = useController({ control, name });

	const file = field.value as File | null;

	const [isDraggingOver, setIsDraggingOver] = useState(false);
	const [selectedFileName, setSelectedFileName] = useState<null | string>(file?.name ?? null);

	const dropZoneRef = useRef<HTMLDivElement>(null);

	const handleFile = useCallback(
		(file: File): void => {
			if (!isFileTypeAllowed(file, acceptedFileTypes)) {
				field.onChange(null);
				setSelectedFileName(null);
				setError(name, {
					message: `File type not allowed. Supported: ${
						acceptedFileTypes
							?.map((t) => {
								return t.split("/").pop()?.toUpperCase();
							})
							.join(", ") ?? "any"
					}`,
					type: "manual",
				});
				return;
			}

			clearErrors(name);
			setSelectedFileName(file.name);
			field.onChange(file);
			onFile?.(file);
		},
		[field, setError, acceptedFileTypes, clearErrors, name, onFile],
	);

	const errorMessage = fieldState.error?.message;
	const isInvalid = Boolean(errorMessage);

	const onDrop = useCallback(
		async (e: DropEvent) => {
			setIsDraggingOver(false);
			const fileItems = e.items.filter((item) => {
				return item.kind === "file";
			});
			if (fileItems.length === 0) {
				return;
			}
			try {
				const file = await fileItems[0].getFile();
				handleFile(file);
			} catch {
				field.onChange(null);
			}
		},
		[field, handleFile],
	);

	return (
		<div className={styles.wrapper}>
			<Label className={clsx("caption", "bold", styles.label)}>{label}</Label>

			<AriaDropZone
				className={clsx(
					styles.dropZone,
					isDraggingOver && styles.dragging,
					isInvalid && styles.invalid,
					isLoading && styles.loading,
					isDisabled && styles.disabled,
				)}
				isDisabled={isLoading || isDisabled}
				onDrop={(e) => {
					void onDrop(e);
				}}
				onDropEnter={() => {
					return setIsDraggingOver(true);
				}}
				onDropExit={() => {
					return setIsDraggingOver(false);
				}}
				ref={dropZoneRef}
			>
				<DropZoneContent
					acceptedFileTypes={acceptedFileTypes}
					isDisabled={isDisabled}
					isLoading={isLoading}
					onFile={handleFile}
					onRemove={() => {
						setSelectedFileName(null);
						field.onChange(null);
					}}
					selectedFileName={selectedFileName}
				/>
			</AriaDropZone>

			{isInvalid && (
				<span
					className={clsx("caption", styles.errorMessage)}
					data-testid="drop-zone-error"
				>
					{errorMessage}
				</span>
			)}
		</div>
	);
}
